import React, { FC, useState, useEffect } from 'react';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { Box } from '~Styles';

import img0032 from '~Assets/Images/LoginScreen/0032.jpg';
import img0059 from '~Assets/Images/LoginScreen/0059.jpg';
import img0073 from '~Assets/Images/LoginScreen/0073.jpg';
import img0122 from '~Assets/Images/LoginScreen/0122.jpg';
import img0128 from '~Assets/Images/LoginScreen/0128.jpg';
import img0132 from '~Assets/Images/LoginScreen/0132.jpg';
import img0138 from '~Assets/Images/LoginScreen/0138.jpg';
import img0148 from '~Assets/Images/LoginScreen/0148.jpg';
import img0188 from '~Assets/Images/LoginScreen/0188.jpg';
import imgTsrPos from '~Assets/Images/LoginScreen/TSR POS 1889.jpg';

const IMAGES = [
  img0032,
  img0059,
  img0073,
  img0122,
  img0128,
  img0132,
  img0138,
  img0148,
  img0188,
  imgTsrPos,
];

const INTERVAL = 7500;
const TRANSITION_DURATION = 1.4;

// ---- Animations ----

const slideInFromRight = keyframes`
  from {
    transform: translateX(6%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
`;

const slideOutToLeft = keyframes`
  from {
    transform: translateX(0);
    opacity: 1;
  }
  to {
    transform: translateX(-6%);
    opacity: 0;
  }
`;

const progressFill = keyframes`
  from { width: 0%; }
  to { width: 100%; }
`;

// ---- Styled Components ----

const Wrapper = styled(Box)`
  flex: 0 0 66%;
  position: relative;
  overflow: hidden;
  border-left: 1px solid var(--colorBlueGray800);

  @media (max-width: 900px) {
    display: none !important;
  }
`;

const baseImageStyles = `
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const ActiveImage = styled.img`
  ${baseImageStyles}
  animation: ${slideInFromRight} ${TRANSITION_DURATION}s cubic-bezier(0.25, 0.1, 0.25, 1) forwards;
`;

const ExitingImage = styled.img`
  ${baseImageStyles}
  animation: ${slideOutToLeft} ${TRANSITION_DURATION}s cubic-bezier(0.25, 0.1, 0.25, 1) forwards;
`;

const ProgressTrack = styled(Box)`
  position: absolute;
  bottom: 24px;
  right: 24px;
  z-index: 2;
  width: 80px;
  height: 3px;
  border-radius: 2px;
  background-color: var(--colorBlueGray800);
  overflow: hidden;
`;

const ProgressBar = styled(Box)`
  height: 100%;
  border-radius: 2px;
  background-color: var(--colorBlueGray50);
  animation: ${progressFill} ${INTERVAL}ms linear forwards;
`;

// ---- Component ----

const ImageCarousel: FC = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [exitingIndex, setExitingIndex] = useState<number | null>(null);

  // Rotate images on interval
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveIndex((prev) => {
        setExitingIndex(prev);
        return (prev + 1) % IMAGES.length;
      });
    }, INTERVAL);

    return () => clearInterval(interval);
  }, []);

  // Clear exiting image after transition completes
  useEffect(() => {
    if (exitingIndex === null) return;

    const timeout = setTimeout(() => {
      setExitingIndex(null);
    }, TRANSITION_DURATION * 1000);

    return () => clearTimeout(timeout);
  }, [exitingIndex]);

  return (
    <Wrapper>
      {/* Exiting image slides out to the left */}
      {exitingIndex !== null && (
        <ExitingImage key={`exit-${exitingIndex}`} src={IMAGES[exitingIndex]} />
      )}

      {/* Active image slides in from the right */}
      <ActiveImage key={`active-${activeIndex}`} src={IMAGES[activeIndex]} />

      {/* Progress bar resets on each image change */}
      <ProgressTrack>
        <ProgressBar key={activeIndex} />
      </ProgressTrack>
    </Wrapper>
  );
};

export default ImageCarousel;
